/**
 * The one place that maps a source — nothing, an existing persona, or a
 * running agent — onto the persona form's defaults, and the form's submit
 * payload back onto a create/update request.
 *
 * Every persona surface (the create/clone/edit dialogs, the full-page form,
 * the launch `@`-mention, "Save as persona") renders the same `PersonaForm`,
 * so they must agree on how a source is projected into it. Keeping the
 * projection here means a field added to the form is wired once instead of
 * five times — the class of bug where one surface silently drops `promptMode`
 * or `groupId` while the others keep it.
 */

import { resolveSandboxOptions, type SandboxOptions } from '@runloop/reflex-contract';
import { defaultOwnerForUser, type Agent, type AgentType } from '@reflex/shared';
import type { AgentPersona } from '../shared/types.js';
import type {
  PersonaConfigDefaults,
  PersonaMetaFormValues,
  PersonaSubmitPayload,
} from './persona-form.js';

export interface PersonaFormDefaults {
  meta: PersonaMetaFormValues;
  config: PersonaConfigDefaults;
}

/**
 * Sandbox capabilities the persona form has no control for and that no control
 * can contradict. They are split off the source's `sandboxOptions`, carried
 * through the form untouched, and merged back on submit so editing a persona
 * (or saving one from a run) never silently drops a capability the form cannot
 * render.
 *
 * Deliberately an allowlist, not "everything the form doesn't render".
 * `blueprintId` and `customSize` are also unrendered, but each shadows a field
 * the form *does* own — a carried-over `blueprintId` collides with a snapshot
 * the user picks (the agent runner rejects launches that set both), and a
 * carried-over `customSize` outranks the size picker at launch. Dropping them
 * on save is what every persona surface did before this module existed.
 */
const PASSTHROUGH_SANDBOX_KEYS = ['computerUse', 'artifactMirrors', 'resumeOnHttp'] as const;

function sandboxPassthrough(options: SandboxOptions | null | undefined): SandboxOptions {
  if (!options) return {};
  const carried: SandboxOptions = {};
  for (const key of PASSTHROUGH_SANDBOX_KEYS) {
    const value = options[key];
    if (value != null) carried[key] = value;
  }
  return carried;
}

/** Defaults for a persona created from scratch. */
export function emptyPersonaDefaults(agentType: AgentType, userId: string): PersonaFormDefaults {
  return {
    meta: {
      name: '',
      description: '',
      icon: null,
      defaultPrompt: '',
      systemPrompt: '',
      owner: defaultOwnerForUser(userId),
    },
    config: {
      agentType,
      promptMode: null,
      model: '',
      modelEndpointId: null,
      providerSecretId: null,
      blueprintName: '',
      snapshotId: null,
      resourceSize: null,
      dockerd: false,
      idleTimeMinutes: null,
      attachments: [],
      envVars: [],
      labelIds: [],
      groupId: null,
      sandboxOptionsPassthrough: {},
    },
  };
}

/** Defaults for editing an existing persona, or (with the owner and name overridden) duplicating one. */
export function personaFormDefaults(persona: AgentPersona): PersonaFormDefaults {
  return {
    meta: {
      name: persona.name,
      description: persona.description ?? '',
      icon: persona.icon ?? null,
      defaultPrompt: persona.defaultPrompt ?? '',
      systemPrompt: persona.systemPrompt ?? '',
      owner: { ownerId: persona.ownerId },
    },
    config: {
      agentType: persona.agentType as AgentType,
      promptMode: persona.promptMode ?? null,
      model: persona.model ?? '',
      modelEndpointId: persona.modelEndpointId ?? null,
      providerSecretId: persona.modelProviderSecretId ?? null,
      blueprintName: persona.sandboxOptions?.blueprintName ?? persona.blueprintName ?? '',
      snapshotId: persona.sandboxOptions?.snapshotId ?? null,
      resourceSize: persona.sandboxOptions?.resourceSize ?? null,
      dockerd: persona.sandboxOptions?.dockerd ?? false,
      idleTimeMinutes: persona.sandboxOptions?.idleTimeMinutes ?? null,
      attachments: persona.attachments ?? [],
      envVars: persona.envVars ?? [],
      labelIds: persona.labelIds ?? [],
      groupId: persona.groupId ?? null,
      sandboxOptionsPassthrough: sandboxPassthrough(persona.sandboxOptions),
    },
  };
}

/**
 * Defaults for "Save as persona": a snapshot of how a run was actually
 * launched. The run's prompt becomes the persona's default prompt (it is the
 * closest thing to "what this persona is for"), and its whole sandbox config,
 * attachment set, env vars, and pinned provider key carry forward. The owner
 * starts as the caller so a user-scoped `mps_*` key stays bindable — an
 * org-owned persona cannot reference one.
 */
export function personaDefaultsFromAgent(agent: Agent, userId: string): PersonaFormDefaults {
  return {
    meta: {
      name: agent.name,
      description: '',
      icon: null,
      // A whitespace-only prompt is not a default prompt.
      defaultPrompt: agent.prompt?.trim() ? agent.prompt : '',
      systemPrompt: agent.systemPrompt ?? '',
      owner: defaultOwnerForUser(userId),
    },
    config: {
      agentType: agent.agentType,
      promptMode: agent.promptMode ?? null,
      model: agent.model ?? '',
      modelEndpointId: null,
      providerSecretId: agent.modelProviderSecretId ?? null,
      blueprintName: agent.sandboxOptions?.blueprintName ?? agent.blueprintName ?? '',
      snapshotId: agent.sandboxOptions?.snapshotId ?? null,
      resourceSize: agent.sandboxOptions?.resourceSize ?? null,
      dockerd: agent.sandboxOptions?.dockerd ?? false,
      idleTimeMinutes: agent.sandboxOptions?.idleTimeMinutes ?? null,
      attachments: agent.attachments ?? [],
      envVars: agent.envVars ?? [],
      // Labels and groups are per-run triage, not part of the run's
      // configuration, so a persona saved from a run starts with none.
      labelIds: [],
      groupId: null,
      sandboxOptionsPassthrough: sandboxPassthrough(agent.sandboxOptions),
    },
  };
}

/** The effective sandbox options for a submitted persona form. */
export function personaSandboxOptions(payload: PersonaSubmitPayload): SandboxOptions | null {
  return resolveSandboxOptions({
    blueprintName: payload.blueprintName,
    sandboxOptions: {
      ...payload.sandboxOptionsPassthrough,
      ...(payload.snapshotId ? { snapshotId: payload.snapshotId } : {}),
      ...(payload.resourceSize ? { resourceSize: payload.resourceSize } : {}),
      ...(payload.dockerd ? { dockerd: true } : {}),
      ...(payload.idleTimeMinutes ? { idleTimeMinutes: payload.idleTimeMinutes } : {}),
    },
  });
}

/** The `POST /agent-personas` body for a submitted persona form. */
export function buildPersonaCreatePayload(payload: PersonaSubmitPayload) {
  const sandboxOptions = personaSandboxOptions(payload);
  return {
    name: payload.name,
    description: payload.description || undefined,
    icon: payload.icon ?? undefined,
    agentType: payload.agentType,
    promptMode: payload.promptMode ?? undefined,
    model: payload.model || undefined,
    modelEndpointId: payload.modelEndpointId ?? undefined,
    providerSecretId: payload.providerSecretId ?? undefined,
    blueprintName: payload.blueprintName || undefined,
    sandboxOptions: sandboxOptions ?? undefined,
    defaultPrompt: payload.defaultPrompt || undefined,
    systemPrompt: payload.systemPrompt || undefined,
    attachments: payload.attachments.length > 0 ? payload.attachments : undefined,
    envVars: payload.envVars.length > 0 ? payload.envVars : undefined,
    labelIds: payload.labelIds.length > 0 ? payload.labelIds : undefined,
    groupId: payload.groupId ?? undefined,
    owner: payload.owner,
  };
}

/** The `PATCH /agent-personas/:id` body for a submitted persona form. */
export function buildPersonaUpdatePayload(payload: PersonaSubmitPayload) {
  const sandboxOptions = personaSandboxOptions(payload);
  return {
    name: payload.name,
    description: payload.description || null,
    icon: payload.icon ?? null,
    agentType: payload.agentType,
    promptMode: payload.promptMode ?? null,
    model: payload.model || null,
    modelEndpointId: payload.modelEndpointId ?? null,
    providerSecretId: payload.providerSecretId ?? null,
    blueprintName: payload.blueprintName || null,
    sandboxOptions: sandboxOptions ?? null,
    defaultPrompt: payload.defaultPrompt || null,
    systemPrompt: payload.systemPrompt || null,
    attachments: payload.attachments.length > 0 ? payload.attachments : null,
    envVars: payload.envVars.length > 0 ? payload.envVars : null,
    labelIds: payload.labelIds.length > 0 ? payload.labelIds : null,
    groupId: payload.groupId ?? null,
  };
}
